import { useState } from "react";
import { FaUserShield, FaLock, FaToggleOn } from "react-icons/fa";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import { toast } from "react-toastify";
import { createUser, updateUser } from "../services/userservice";
import { getUserStatusLabel, getUserStatusClass } from "../../constants/constants";

const buildInitialForm = (editUser) => {
  if (!editUser) {
    return { employee: "", email: "", first_name: "", last_name: "", role_ids: [], is_active: true };
  }
  const currentRoleIds = (editUser.roles && editUser.roles.length > 0)
    ? editUser.roles.map((r) => (typeof r === "object" ? r.id : r))
    : (editUser.role_id ? [editUser.role_id] : []);
  return {
    employee: editUser.employee || "",
    email: editUser.email || "",
    first_name: editUser.first_name || "",
    last_name: editUser.last_name || "",
    role_ids: currentRoleIds,
    is_active: editUser.is_active ?? true,
  };
};

const UserFormModal = ({ editUser, employees = [], roles = [], onClose, onSaved }) => {
  // Defensive guard: never trust a prop to actually be an array, even
  // though the parent normalizes it. This is what stops the
  // "Cannot read properties of undefined (reading 'map')" crash for good —
  // the modal degrades to empty lists instead of throwing.
  const safeEmployees = Array.isArray(employees) ? employees : [];
  const safeRoles = Array.isArray(roles) ? roles : [];

  const [form, setForm]               = useState(() => buildInitialForm(editUser));
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // Single source of truth for toggling a role checkbox — driven by the
  // checkbox's own onChange instead of a label-level preventDefault hack,
  // so both clicking the checkbox and clicking the label text work reliably.
  const toggleRole = (roleId) => {
    const current = new Set(form.role_ids || []);
    if (current.has(roleId)) current.delete(roleId);
    else current.add(roleId);
    setForm((prev) => ({ ...prev, role_ids: Array.from(current) }));
    setFieldErrors((prev) => ({ ...prev, role_ids: null }));
  };

  const handleSave = async () => {
    if (!form.email) {
      setFieldErrors({ email: ["Email is required"] });
      return setError("Please check the field errors below.");
    }
    setSaving(true);
    try {
      const payload = { ...form, role_ids: Array.isArray(form.role_ids) ? form.role_ids : (form.role_id ? [form.role_id] : []) };
      if (editUser) {
        await updateUser(editUser.id, payload);
        toast.success("User updated");
      } else {
        const res = await createUser(payload);
        const tempPw = res.data?.temporary_password || res.temporary_password;
        if (tempPw) toast.success(`User created. Temp password: ${tempPw}`, { autoClose: 8000 });
        else toast.success("User created");
      }
      onSaved();
    } catch (err) {
      const resData = err.response?.data;
      let fErrors = {};
      let topMsg = "";
      if (resData && typeof resData === "object") {
        if (resData.errors && typeof resData.errors === "object" && Object.keys(resData.errors).length > 0) {
          fErrors = resData.errors;
          topMsg = resData.errors.non_field_errors ? (Array.isArray(resData.errors.non_field_errors) ? resData.errors.non_field_errors.join(" ") : resData.errors.non_field_errors) : "Please check the field errors below.";
        } else if (!resData.message && !resData.success && Object.keys(resData).length > 0) {
          fErrors = resData;
          topMsg = "Please check the field errors below.";
        } else {
          topMsg = resData.message || resData.detail || "Failed to save user";
        }
      } else {
        topMsg = "Failed to save user.";
      }
      setFieldErrors(fErrors);
      setError(topMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={editUser ? "Edit User" : "Add User"} onClose={onClose} size="lg">
      {error && <div className="form-error">{error}</div>}

      <div className="employee-form-modal-body">
        <div className="form-section-card">
          <div className="section-title-premium"><FaUserShield /> User Information</div>
          <div className="grid-3">
            <div>
              <input className={fieldErrors.first_name ? "input-error" : ""} placeholder="First Name" value={form.first_name} onChange={(e) => { setForm({ ...form, first_name: e.target.value }); setFieldErrors({ ...fieldErrors, first_name: null }); }} />
              {fieldErrors.first_name && <span className="field-error">{Array.isArray(fieldErrors.first_name) ? fieldErrors.first_name[0] : fieldErrors.first_name}</span>}
            </div>
            <div>
              <input className={fieldErrors.last_name ? "input-error" : ""} placeholder="Last Name" value={form.last_name} onChange={(e) => { setForm({ ...form, last_name: e.target.value }); setFieldErrors({ ...fieldErrors, last_name: null }); }} />
              {fieldErrors.last_name && <span className="field-error">{Array.isArray(fieldErrors.last_name) ? fieldErrors.last_name[0] : fieldErrors.last_name}</span>}
            </div>
            <div>
              <input type="email" className={fieldErrors.email ? "input-error" : ""} placeholder="Email *" value={form.email} readOnly={!!editUser} onChange={(e) => { setForm({ ...form, email: e.target.value }); setFieldErrors({ ...fieldErrors, email: null }); setError(""); }} style={{ opacity: editUser ? 0.6 : 1 }} />
              {fieldErrors.email && <span className="field-error">{Array.isArray(fieldErrors.email) ? fieldErrors.email[0] : fieldErrors.email}</span>}
            </div>
          </div>

          {!editUser && (
            <div className="full-row" style={{ marginTop: 16 }}>
              <Dropdown
                options={safeEmployees.map((e) => ({ label: `${e.full_name || e.name} (${e.emp_id})`, value: e.id }))}
                value={form.employee}
                onChange={(v) => { setForm({ ...form, employee: v }); setFieldErrors({ ...fieldErrors, employee: null }); }}
                placeholder="Linked Employee (Optional)"
              />
              {fieldErrors.employee && <span className="field-error">{Array.isArray(fieldErrors.employee) ? fieldErrors.employee[0] : fieldErrors.employee}</span>}
            </div>
          )}
        </div>

        <div className="form-section-card">
          <div className="section-title-premium"><FaLock /> Role Assignment</div>
          <div className="role-selector-premium">
            {safeRoles.map((r) => {
              const isSelected = (form.role_ids || []).includes(r.id);
              return (
                <label key={r.id} className={`role-pill ${isSelected ? "selected" : ""}`}>
                  <input type="checkbox" checked={isSelected} onChange={() => toggleRole(r.id)} />
                  <span>{r.name?.toUpperCase()} <small>({Number(r.data_scope) === 1 ? "Self" : Number(r.data_scope) === 2 ? "Reportees" : Number(r.data_scope) === 3 ? "Department" : Number(r.data_scope) === 4 ? "All" : r.data_scope})</small></span>
                </label>
              );
            })}
            {safeRoles.length === 0 && <span className="role-selector-empty">No roles found</span>}
          </div>
          {fieldErrors.role_ids && <span className="field-error">{Array.isArray(fieldErrors.role_ids) ? fieldErrors.role_ids[0] : fieldErrors.role_ids}</span>}
        </div>

        <div className="form-section-card">
          <div className="section-title-premium"><FaToggleOn /> Account Status</div>
          <div className="status-toggle">
            <div className={`toggle-switch ${form.is_active ? "active" : ""}`} onClick={() => setForm({ ...form, is_active: !form.is_active })}>
              <div className="toggle-knob"></div>
            </div>
            <span className={`status-label ${getUserStatusClass(form.is_active)}`}>
              {getUserStatusLabel(form.is_active)}
            </span>
          </div>
        </div>
      </div>

      <div className="premium-modal-footer">
        <button className="user-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
};

export default UserFormModal;